/**
 * 社交 / 投放常见 In-App Browser（IAB）检测。
 *
 * 行业原则（与本项目一致）：
 * - 不把 `window.webkit.messageHandlers` 当核心能力，只作可选增强；
 * - 调用原生桥前必须存在性判断（见 `nativeBridge.js`）；
 * - 支付等关键路径：在 IAB 内提示用户用系统浏览器打开（iOS 上无稳定的「强制外链」API，勿依赖 `window.open(..., '_system')`）。
 *
 * 本地调试：
 * - `localStorage.setItem('DEBUG_SOCIAL_IAB','1')` 后刷新 → 强制视为社交 IAB（降级条、PayPal 探测等）
 * - `localStorage.setItem('DEBUG_AIRWALLEX_SDK_DEGRADE','1')` → 强制 Airwallex SDK 降级 UI（需 DEV + 上一项）
 * 勿在生产依赖这些开关。
 */

/** @param {string} [userAgent] */
export function isSocialInAppBrowserUA(userAgent) {
  const ua = String(userAgent || '')
  if (!ua) return false
  return (
    /\bfban|fbav|fb_iab\b/i.test(ua) ||
    /\binstagram\b/i.test(ua) ||
    /\b(tiktok|bytedancewebview|musical_ly|trill_\d+|aweme)\b/i.test(ua)
  )
}

/** @param {string} [userAgent] */
export function isIOSUserAgent(userAgent) {
  return /iphone|ipad|ipod/i.test(String(userAgent || ''))
}

/** 当前是否为 FB / IG / TikTok 等内嵌浏览器（不限 iOS，充值提示等用） */
export function isSocialInAppBrowser() {
  if (typeof navigator === 'undefined') return false
  if (import.meta.env.DEV && typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_SOCIAL_IAB') === '1') {
    return true
  }
  return isSocialInAppBrowserUA(navigator.userAgent)
}

/** iOS + 社交 IAB：`webkitBridgeGuard` 与部分 Sentry 降噪逻辑与此对齐 */
export function isIOSocialInAppBrowser() {
  if (typeof navigator === 'undefined') return false
  if (import.meta.env.DEV && typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_SOCIAL_IAB') === '1') {
    return isIOSUserAgent(navigator.userAgent || '')
  }
  const ua = navigator.userAgent || ''
  return isIOSUserAgent(ua) && isSocialInAppBrowserUA(ua)
}

export function getHrefForExternalOpen() {
  if (typeof window === 'undefined') return ''
  return window.location.href
}

/** 复制任意字符串到剪贴板 */
export async function copyTextToClipboard(text) {
  const s = String(text || '')
  if (!s) return false
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(s)
      return true
    }
  } catch (_) {}
  try {
    const ta = document.createElement('textarea')
    ta.value = s
    ta.setAttribute('readonly', '')
    ta.style.cssText = 'position:fixed;left:-9999px;top:0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch (_) {
    return false
  }
}

/** 复制当前页 URL，供用户粘贴到系统浏览器打开 */
export async function copyCurrentPageUrlToClipboard() {
  return copyTextToClipboard(getHrefForExternalOpen())
}

/**
 * 跳转 Airwallex / PayPal 等 Hosted Checkout。
 * 社交 IAB 内首次 `location.href` 常被拦截；若超时仍停留在原站则 `location.replace` 重试一次。
 */
export function navigateToHostedCheckout(url, { iabFallbackMs = 1200 } = {}) {
  if (typeof window === 'undefined') return false
  const target = String(url || '').trim()
  if (!target) return false

  const appOrigin = window.location.origin
  window.location.href = target

  if (!isSocialInAppBrowser()) return true

  let leftPage = false
  const markLeft = () => {
    leftPage = true
  }
  window.addEventListener('pagehide', markLeft, { once: true })
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') markLeft()
  }
  document.addEventListener('visibilitychange', onVisibility)

  window.setTimeout(() => {
    window.removeEventListener('pagehide', markLeft)
    document.removeEventListener('visibilitychange', onVisibility)
    if (leftPage) return
    if (document.visibilityState === 'visible' && window.location.origin === appOrigin) {
      window.location.replace(target)
    }
  }, iabFallbackMs)

  return true
}
